import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { BentoBrand } from "schemas/dashboard";

import { formatNumber } from "utils/string";

interface BrandSearchPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  brands: BentoBrand[];
  loading: boolean;
}

export default function BrandSearchPreviewDialog({
  open,
  onClose,
  brands,
  loading,
}: BrandSearchPreviewDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#014339" }}>
          Search Preview
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Box
            component="span"
            className="fa-solid fa-xmark"
            sx={{ fontSize: 14, color: "#014339" }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: "primary.main" }} />
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              Searching for matching brands...
            </Typography>
          </Box>
        ) : brands.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Box
              component="span"
              className="fa-solid fa-magnifying-glass"
              sx={{ fontSize: 28, color: "rgba(1,67,57,0.2)", mb: 2 }}
            />
            <Typography sx={{ fontSize: 15, color: "text.secondary", mt: 1 }}>
              No brands matched your search criteria. Try broadening your
              filters.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>
              {brands.length} brand{brands.length !== 1 ? "s" : ""} found
            </Typography>
            {brands.map((brand) => (
              <PreviewBrandCard key={brand.id} brand={brand} />
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewBrandCard({ brand }: { brand: BentoBrand }) {
  const displayName =
    brand.normalizedName || brand.brandName || "Unknown Brand";

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid rgba(1,67,57,0.1)",
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Avatar
          src={brand.logoUrl}
          sx={{
            width: 44,
            height: 44,
            backgroundColor: brand.backgroundLogoColor || "#e5e7eb",
            fontSize: 16,
            fontWeight: 600,
            color: "#014339",
            flexShrink: 0,
          }}
        >
          {displayName[0]?.toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              lineHeight: "22px",
              color: "#014339",
            }}
          >
            {displayName}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mt: 0.25,
            }}
          >
            {brand.instagramUsername && (
              <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                @{brand.instagramUsername}
              </Typography>
            )}
            {brand.instagramFollowerCount != null && (
              <>
                {brand.instagramUsername && (
                  <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                    &bull;
                  </Typography>
                )}
                <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.5)" }}>
                  {formatNumber(brand.instagramFollowerCount)} followers
                </Typography>
              </>
            )}
          </Box>

          {/* Links */}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            {brand.website && (
              <LinkChip
                href={`https://${brand.website}`}
                icon="fa-solid fa-globe"
                label="Website"
              />
            )}
            {brand.instagramUsername && (
              <LinkChip
                href={`https://instagram.com/${brand.instagramUsername}`}
                icon="fa-brands fa-instagram"
                label="Instagram"
              />
            )}
          </Box>
        </Box>
      </Box>

      {brand.aboutSummary && <AboutSummary text={brand.aboutSummary} />}
    </Box>
  );
}

function AboutSummary({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: "1px solid rgba(1,67,57,0.08)",
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          color: "rgba(1,67,57,0.7)",
          lineHeight: "20px",
          display: "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 2,
          WebkitBoxOrient: "vertical",
          overflow: expanded ? "visible" : "hidden",
        }}
      >
        {text}
      </Typography>
      {text.length > 120 && (
        <Typography
          component="span"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#014339",
            cursor: "pointer",
            mt: 0.25,
            display: "inline-block",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Typography>
      )}
    </Box>
  );
}

function LinkChip({
  href,
  icon,
  label,
}: {
  href: string;
  icon: string;
  label: string;
}) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{ textDecoration: "none" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          border: "1px solid rgba(1,67,57,0.2)",
          borderRadius: "4px",
          px: "9px",
          py: "1px",
          backgroundColor: "white",
          cursor: "pointer",
          "&:hover": { backgroundColor: "rgba(1,67,57,0.03)" },
        }}
      >
        <Box
          component="span"
          className={icon}
          sx={{ fontSize: 10, color: "#014339" }}
        />
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 500,
            color: "#014339",
            lineHeight: "15px",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Link>
  );
}
